/**
 * Bug 37038, "split the access" (Melina Marquez, HSE, 2026-10-28): the form
 * detail route serves both a NEW fill and `?dynamicResponseId=<id>` for an
 * EXISTING record. A new fill needs only the category view; another person's
 * record needs the open-any-record right (caneditforms EditAll today); the
 * caller's own record needs only the category view.
 *
 * This gates the page, not the data. The Dynamic Content API is the boundary:
 * GET checks CanViewSubmission and a save of an existing id checks
 * CanUpdateSubmission (Bug 36809).
 */
export type FormDetailAccess = 'granted' | 'denied' | 'owner-only';

export interface FormDetailAccessInput {
  canViewCategory: boolean;
  isExistingRecord: boolean;
  canOpenAnyRecord: boolean;
}

export function formDetailAccess(
  input: FormDetailAccessInput
): FormDetailAccess {
  if (!input.canViewCategory) {
    return 'denied';
  }
  if (!input.isExistingRecord || input.canOpenAnyRecord) {
    return 'granted';
  }
  return 'owner-only';
}

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

/**
 * Whether the record's creator is the caller. An empty or all-zero id on either
 * side never matches, the same fail-closed rule as the server's
 * FormCommentAuthorizationService.
 */
export function isOwnFormResponse(
  createdBy: string | null | undefined,
  callerUserId: string | null | undefined
): boolean {
  const owner = (createdBy ?? '').trim().toLowerCase();
  const caller = (callerUserId ?? '').trim().toLowerCase();
  if (!owner || !caller || owner === EMPTY_GUID || caller === EMPTY_GUID) {
    return false;
  }
  return owner === caller;
}
